/**
 * Le fil — la raison de se connecter le matin.
 *
 * Ordre voulu : ce qui attend une réponse de moi (demandes de rencontre),
 * puis ce qui s'est passé sans moi (vues, clics, nouveaux inscrits), puis ce
 * que je devrais faire (événements recommandés), puis ce que j'ai déjà fait
 * (événements passés et leurs résultats). Jamais un chiffre nu : chaque
 * nombre est cliquable ou expliqué, sinon c'est de la décoration.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Eye, MousePointerClick, Sparkles, Users } from 'lucide-react';
import { appelerApi } from '../../reseau.ts';
import { Badge, Card, PageTitle, SectionLabel } from '../../admin/ui.tsx';
import { CarteEntrepreneur } from './CarteEntrepreneur.tsx';
import { dateLongue, nomDe, quandRelatif, reseauDe, type Espace, type Flux } from './types.ts';
import { EncartParrainage } from './EncartParrainage.tsx';
import { PartagerMonLien } from './PartagerMonLien.tsx';

export function Fil({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const [flux, setFlux] = useState<Flux | null>(null);

  const charger = async () => {
    const r = await appelerApi<Flux>('/api/compte/flux');
    if (r.donnees) setFlux(r.donnees);
  };
  useEffect(() => {
    void charger();
  }, []);

  const p = espace.profil;
  const manquants = [
    !p?.photo_url && 'votre photo',
    !p?.entreprise && 'votre entreprise',
    !p?.services && !p?.pitch && 'ce que vous proposez',
    !p?.recherche && 'ce que vous cherchez',
    (p?.forces?.length ?? 0) === 0 && 'vos forces',
    (p?.sujets_conversation?.length ?? 0) === 0 && 'vos sujets hors travail',
  ].filter(Boolean) as string[];

  const n = flux?.nouveautes;

  return (
    <>
      <PageTitle
        title={
          <>
            Bonjour {espace.identite.prenom ?? ''},{' '}
            <em className="text-match-gradient font-serif italic font-medium">bienvenue</em>
          </>
        }
        subtitle="Ce qui a bougé depuis votre dernière visite, et ce qui mérite votre journée."
      />

      <div className="mb-6 flex flex-col gap-3">
        <PartagerMonLien espace={espace} />
        <EncartParrainage />
      </div>

      {p?.pitch_brouillon && (
        <Card className="mb-6 border border-graphite">
          <SectionLabel>Votre pitch vous attend</SectionLabel>
          <p className="mt-2 max-w-[640px] text-[14px] leading-relaxed text-fumee">
            L’IA a rédigé une présentation de votre entreprise à partir de votre site. Relisez-la :
            une fois validée, c’est elle qui sert à vous rapprocher des bons interlocuteurs.
          </p>
          <Link to="/compte/entreprise" className="btn-creme mt-4 inline-block !min-h-0 px-4 py-2 text-[13px]">
            Relire et valider
          </Link>
        </Card>
      )}

      {manquants.length > 0 && (
        <Card className="mb-6">
          <SectionLabel>Complétez votre profil</SectionLabel>
          <p className="mt-2 max-w-[640px] text-[14px] leading-relaxed text-fumee">
            Il manque {manquants.slice(0, 3).join(', ')}
            {manquants.length > 3 ? `, +${manquants.length - 3}` : ''}. C’est exactement ce qui
            permet de vous proposer des partenariats crédibles plutôt que des noms au hasard.
          </p>
          <Link to="/compte/entreprise" className="btn-creme mt-4 inline-block !min-h-0 px-4 py-2 text-[13px]">
            Compléter
          </Link>
        </Card>
      )}

      {n && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Compteur icone={<Users size={14} strokeWidth={1.5} />} valeur={n.demandes} libelle="demande(s) de rencontre" vers="/compte/rendez-vous" />
          <Compteur icone={<Sparkles size={14} strokeWidth={1.5} />} valeur={n.matchs} libelle="nouveau(x) match(s)" vers="/compte/rendez-vous" />
          <Compteur icone={<Eye size={14} strokeWidth={1.5} />} valeur={n.vues} libelle="vue(s) de votre profil" />
          <Compteur icone={<MousePointerClick size={14} strokeWidth={1.5} />} valeur={n.clics} libelle="clic(s) sur vos liens" />
        </div>
      )}

      {flux === null && <p className="text-[14px] text-fumee">Chargement…</p>}

      {flux && flux.demandes_recues.length > 0 && (
        <Bloc titre="On veut vous rencontrer" sous="À vous de répondre — un match débloque vos coordonnées des deux côtés.">
          <div className="grid gap-4 sm:grid-cols-2">
            {flux.demandes_recues.slice(0, 4).map((d) => (
              <CarteEntrepreneur key={d.personne_id} carte={d} onChange={() => void charger()} />
            ))}
          </div>
        </Bloc>
      )}

      {flux && flux.nouveaux_compatibles.length > 0 && (
        <Bloc titre="Nouveaux inscrits qui vous correspondent" sous="Arrivés ces quinze derniers jours.">
          <div className="grid gap-4 sm:grid-cols-2">
            {flux.nouveaux_compatibles.map((c) => (
              <CarteEntrepreneur key={c.personne_id} carte={c} onChange={() => void charger()} />
            ))}
          </div>
        </Bloc>
      )}

      {flux && flux.evenements_recommandes.length > 0 && (
        <Bloc titre="Les événements à ne pas manquer" sous="Choisis pour vous : d’abord ceux où votre public est déjà inscrit.">
          <div className="grid gap-4 lg:grid-cols-3">
            {flux.evenements_recommandes.map((e) => (
              <Card key={e.id} className="flex flex-col">
                <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
                  {e.nom}
                  {e.aujourdhui && <Badge filled>Aujourd’hui</Badge>}
                </p>
                <p className="mt-1 text-[13px] text-fumee">
                  {dateLongue(e.debut_le)}
                  {e.lieu_ville ? ` · ${e.lieu_ville}` : ''}
                </p>
                <p className="mt-3 flex-1 text-[13px] leading-relaxed text-blanc">{e.raison}</p>
                <Link to="/compte/evenements" className="mt-4 text-[13px] text-fumee underline hover:text-blanc">
                  Voir et rejoindre
                </Link>
              </Card>
            ))}
          </div>
        </Bloc>
      )}

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        {flux && (
          <Card>
            <SectionLabel>Qui a regardé votre profil</SectionLabel>
            {flux.vues.total === 0 ? (
              <p className="mt-3 text-[14px] leading-relaxed text-fumee">
                Personne encore. Votre profil se montre quand vous demandez des rencontres et quand
                vous rejoignez des événements.
              </p>
            ) : (
              <>
                <p className="display-title mt-2 text-[28px] text-blanc">{flux.vues.total}</p>
                <p className="text-[12px] text-fumee">vue(s) ces sept derniers jours</p>
                <ul className="mt-3 flex flex-col gap-2">
                  {flux.vues.recentes.slice(0, 6).map((v, i) => (
                    <li key={i} className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
                      <span className="text-blanc">
                        {v.par_id ? nomDe(v) : 'Visiteur discret'}
                        {v.entreprise && <span className="text-fumee"> · {v.entreprise}</span>}
                      </span>
                      <span className="text-fumee">{quandRelatif(v.vu_le)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        )}

        {flux && (
          <Card>
            <SectionLabel>Vos liens</SectionLabel>
            {flux.liens.total === 0 ? (
              <p className="mt-3 text-[14px] leading-relaxed text-fumee">
                Aucun clic pour l’instant. Ajoutez votre site et vos réseaux dans « Moi et mon
                entreprise » : chaque clic est compté et vous dit ce qui intéresse.
              </p>
            ) : (
              <>
                <p className="display-title mt-2 text-[28px] text-blanc">{flux.liens.total}</p>
                <p className="text-[12px] text-fumee">clic(s) sur trente jours</p>
                <ul className="mt-3 flex flex-col gap-2">
                  {flux.liens.par_lien.slice(0, 5).map((l) => (
                    <li key={l.url} className="flex items-baseline justify-between gap-2 text-[13px]">
                      <span className="truncate text-blanc">{reseauDe(l.url)}</span>
                      <span className="shrink-0 text-fumee">{l.clics}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        )}
      </div>

      {flux && flux.classement.top.length > 0 && (
        <Bloc titre="Les entreprises qui font parler d’elles" sous="Vues, clics et demandes reçues sur trente jours.">
          <Card>
            <ol className="flex flex-col gap-2">
              {flux.classement.top.map((r) => (
                <li
                  key={r.personne_id}
                  className={`flex flex-wrap items-baseline justify-between gap-2 rounded-[6px] px-2 py-1.5 text-[13px] ${r.moi ? 'bg-void' : ''}`}
                >
                  <span className="min-w-0">
                    <span className="mr-2 text-fumee">{r.rang}.</span>
                    <span className={r.moi ? 'text-match-gradient font-medium' : 'text-blanc'}>
                      {r.entreprise ?? nomDe(r)}
                      {r.moi && ' — vous'}
                    </span>
                    {r.secteur && <span className="text-fumee"> · {r.secteur}</span>}
                  </span>
                  <span className="shrink-0 text-fumee">
                    {r.vues} vues · {r.clics} clics · {r.demandes} demandes
                  </span>
                </li>
              ))}
            </ol>
            {flux.classement.moi && !flux.classement.top.some((t) => t.moi) && (
              <p className="mt-3 border-t border-graphite pt-3 text-[13px] text-fumee">
                Vous êtes <span className="text-match-gradient font-medium">{flux.classement.moi.rang}</span>
                <sup>e</sup> sur {flux.classement.total} — complétez votre profil et demandez des
                rencontres pour remonter.
              </p>
            )}
          </Card>
        </Bloc>
      )}

      {flux && flux.evenements_passes.length > 0 && (
        <Bloc titre="Ce que vos événements ont donné" sous="Contacts échangés et rendez-vous tenus, événement par événement.">
          <Card>
            <ul className="flex flex-col gap-3">
              {flux.evenements_passes.map((e) => (
                <li key={e.evenement_id} className="flex flex-wrap items-baseline justify-between gap-2 border-b border-graphite pb-3 text-[13px] last:border-b-0 last:pb-0">
                  <span>
                    <span className="text-blanc">{e.nom}</span>
                    <span className="text-fumee"> · {dateLongue(e.debut_le)}{e.lieu_ville ? `, ${e.lieu_ville}` : ''}</span>
                  </span>
                  <span className="shrink-0 text-fumee">
                    {e.contacts} contact(s) · {e.rendez_vous} rendez-vous
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </Bloc>
      )}

      <button onClick={() => void Promise.all([charger(), onChange()])} className="mt-8 text-[13px] text-fumee hover:text-blanc">
        Rafraîchir
      </button>
    </>
  );
}

function Compteur({ icone, valeur, libelle, vers }: { icone: ReactNode; valeur: number; libelle: string; vers?: string }) {
  const contenu = (
    <Card className={valeur > 0 ? 'border border-graphite' : ''}>
      <p className="label-caps flex items-center gap-1.5 text-fumee">
        {icone}
        {libelle}
      </p>
      <p className={`display-title mt-2 text-[26px] ${valeur > 0 ? 'text-match-gradient' : 'text-fumee'}`}>{valeur}</p>
    </Card>
  );
  return vers && valeur > 0 ? <Link to={vers}>{contenu}</Link> : contenu;
}

function Bloc({ titre, sous, children }: { titre: string; sous: string; children: ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-[18px] font-light text-blanc">{titre}</h2>
      <p className="mb-4 mt-1 max-w-[640px] text-[13px] leading-relaxed text-fumee">{sous}</p>
      {children}
    </section>
  );
}
